import { Component, forwardRef, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import JsBarcode from 'jsbarcode';

import { languageService, languageSupport } from '@unpispas/upp-base';

@Component({
    selector: 'upp-barcode-display',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-display.html',
    styleUrls: [
        './upp-display.scss'
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UppBarcodeDisplayComponent),
            multi: true,
        },
    ],
})
export class UppBarcodeDisplayComponent extends languageSupport implements ControlValueAccessor, OnDestroy {
    private static readonly _BarcodeRenderOptions = {
        displayValue: false,
        height: 40,
        margin: 4,
        width: 2,
    };

    /** `'EDIT'`: assign / update / remove; `'VIEW'`: read-only preview. */
    @Input() mode: 'VIEW' | 'EDIT' = 'EDIT';

    @Input() disabled = false;

    @ViewChild('svg') private _svg?: ElementRef<SVGSVGElement>;

    ImageVisible = false;

    private _listening = false;
    private _value: string | null = null;

    /* eslint-disable @typescript-eslint/no-unused-vars */
    private _onChangeCallback = (_value: string | null) => {
        // required by angular
    };

    private _onTouchCallback = () => {
        // required by angular
    };
    /* eslint-enable @typescript-eslint/no-unused-vars */

    get Listening(): boolean {
        return this._listening;
    }

    get TextValue(): string {
        return String(this._value || '').trim();
    }

    get HasValue(): boolean {
        return !!this.TextValue;
    }

    constructor(
        lang: languageService,
        change: ChangeDetectorRef,
    ) {
        super(lang, change);
    }

    ngOnDestroy(): void {
        this._listening = false;
        this.OnDestroy();
    }

    OnAssign(): void {
        if (this.disabled || this.mode !== 'EDIT'){
            return;
        }

        this._listening = true;
        this._chng?.markForCheck();
    }

    OnScanCancel(): void {
        this._listening = false;
        this._chng?.markForCheck();
    }

    OnScanCode(code: string): void {
        const _value = String(code ?? '').trim();
        if (!_value){
            return;
        }

        this._listening = false;
        this._ApplyValue(_value, true);
    }

    OnRemove(): void {
        if (this.disabled || this.mode !== 'EDIT'){
            return;
        }

        this._listening = false;
        this._ApplyValue(null, true);
    }

    writeValue(value: unknown): void {
        this._ApplyValue(UppBarcodeDisplayComponent._NormalizeBarcodeValue(value), false);
    }

    registerOnChange(fn: (value: string | null) => void): void {
        this._onChangeCallback = fn;
    }

    registerOnTouched(fn: () => void): void {
        this._onTouchCallback = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        if (isDisabled){
            this._listening = false;
        }
        this._chng?.markForCheck();
    }

    private _ApplyValue(next: string | null, notify: boolean): void {
        if (next === this._value){
            return;
        }

        this._value = next;

        if (notify){
            this._onChangeCallback(next);
            this._onTouchCallback();
        }

        if (next){
            setTimeout(() => this._Render());
        }
        else {
            this.ImageVisible = false;
        }

        this._chng?.markForCheck();
    }

    private _Render(): void {
        const _value = this.TextValue;
        const _svg = this._svg?.nativeElement;

        if (!_svg){
            return;
        }

        while (_svg.firstChild){
            _svg.removeChild(_svg.firstChild);
        }

        if (!_value){
            this.ImageVisible = false;
            this._chng?.markForCheck();
            return;
        }

        const _formats = UppBarcodeDisplayComponent._BarcodeFormatsFor(_value);
        this.ImageVisible = false;
        for (const _format of _formats){
            try {
                JsBarcode(_svg, _value, { ...UppBarcodeDisplayComponent._BarcodeRenderOptions, format: _format });
                this.ImageVisible = true;
                break;
            }
            catch {
                while (_svg.firstChild){
                    _svg.removeChild(_svg.firstChild);
                }
            }
        }

        if (!this.ImageVisible){
            this._chng?.markForCheck();
            return;
        }

        this._chng?.markForCheck();
    }

    private static _NormalizeBarcodeValue(raw: unknown): string | null {
        const _value = String(raw ?? '').trim();
        return _value || null;
    }

    private static _BarcodeFormatsFor(value: string): string[] {
        if (/^\d{13}$/.test(value)){
            return ['EAN13', 'CODE128'];
        }

        return ['CODE128'];
    }
}
